import React, { useState, useEffect, useRef, Fragment } from 'react';
import { getFirestore, collection, getDocs, query, orderBy } from 'firebase/firestore';
import { format } from 'date-fns';
import CandidateProfileTab from '../candidate-progress/CandidateProfileTab';
import { XMarkIcon, CheckIcon } from '@heroicons/react/24/outline';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { toast } from 'react-hot-toast';
import { Dialog, Transition } from '@headlessui/react';

export default function WaitlistManagementView() {
  const [waitlistEntries, setWaitlistEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [showProfileSlideOut, setShowProfileSlideOut] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [showOutreachModal, setShowOutreachModal] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const selectAllCheckboxRef = useRef(null);

  // Set indeterminate state for the select all checkbox
  useEffect(() => {
    if (selectAllCheckboxRef.current) {
      const isIndeterminate = selectedIds.length > 0 && selectedIds.length < waitlistEntries.length;
      selectAllCheckboxRef.current.indeterminate = isIndeterminate;
    }
  }, [selectedIds.length, waitlistEntries.length]);

  // Helper function to format timestamps
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    
    try {
      // Check if timestamp is a Firestore timestamp
      if (timestamp.toDate) {
        return format(timestamp.toDate(), 'MMM d, yyyy, h:mm a');
      }
      
      // Check if timestamp is a string (ISO format)
      if (typeof timestamp === 'string') {
        return format(new Date(timestamp), 'MMM d, yyyy, h:mm a');
      }
      
      return 'Invalid date';
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Invalid date';
    }
  };

  // Helper function to format date only (no time)
  const formatDateOnly = (timestamp) => {
    if (!timestamp) return 'N/A';
    
    try {
      // Check if timestamp is a Firestore timestamp
      if (timestamp.toDate) {
        return format(timestamp.toDate(), 'MMM d, yyyy');
      }
      
      // Check if timestamp is a string (ISO format)
      if (typeof timestamp === 'string') {
        return format(new Date(timestamp), 'MMM d, yyyy');
      }
      
      return 'Invalid date';
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Invalid date';
    }
  };

  // Convert waitlist entry to user format for CandidateProfileTab
  const convertEntryToUser = (entry) => {
    return {
      id: entry.id,
      email: entry.userInfo?.email,
      displayName: entry.userInfo?.firstName && entry.userInfo?.lastName 
        ? `${entry.userInfo.firstName} ${entry.userInfo.lastName}`
        : entry.userInfo?.firstName || entry.userInfo?.lastName || 'Unknown User',
      createdAt: entry.created_at,
      lastLogin: null,
      basicInfo: {
        firstName: entry.userInfo?.firstName,
        lastName: entry.userInfo?.lastName,
        linkedinUrl: entry.userInfo?.linkedinUrl,
        email: entry.userInfo?.email
      },
      waitlistData: {
        submittedAt: entry.completedAt,
        quizAnswers: entry.answers || {}
      },
      hasResume: entry.hasResume,
      resumeUrl: entry.resumeUrl || entry.resume?.url || entry.resume?.resumeUrl,
      resumeFilename: entry.resumeFilename || entry.resume?.filename || entry.resume?.name || (entry.hasResume ? 'Resume' : null)
    };
  };

  // Handle row click to show profile
  const handleEntryClick = (entry) => {
    const userFormat = convertEntryToUser(entry);
    setSelectedEntry(userFormat);
    setShowProfileSlideOut(true);
  };

  // Close profile slide out
  const handleCloseProfile = () => {
    setShowProfileSlideOut(false);
    setTimeout(() => setSelectedEntry(null), 300); // Delay to allow animation
  };

  // Handle individual candidate selection
  const handleCheckboxChange = (entryId, e) => {
    e.stopPropagation(); // Prevent row click event from firing
    
    if (selectedIds.includes(entryId)) {
      setSelectedIds(selectedIds.filter(id => id !== entryId));
    } else {
      setSelectedIds([...selectedIds, entryId]);
    }
  };
  
  // Handle select all checkbox
  const handleSelectAll = (e) => {
    e.stopPropagation();
    
    if (selectedIds.length === waitlistEntries.length) {
      // If all are selected, deselect all
      setSelectedIds([]);
    } else {
      // Otherwise select all
      setSelectedIds(waitlistEntries.map(entry => entry.id));
    }
  };

  const handleSendOutreach = async () => {
    if (selectedIds.length === 0) {
      toast.error('No users selected.');
      return;
    }

    setIsSending(true);
    try {
      const functions = getFunctions();
      const sendWaitlistOutreachEmail = httpsCallable(functions, 'sendWaitlistOutreachEmail');
      const result = await sendWaitlistOutreachEmail({ entryIds: selectedIds });

      const { successCount, failCount } = result.data;

      if (failCount > 0) {
        toast.error(`${failCount} emails failed to send.`);
      }
      if (successCount > 0) {
        toast.success(`Successfully sent ${successCount} outreach email(s).`);
      }
      setShowOutreachModal(false);
      setSelectedIds([]);
    } catch (error) {
      console.error("Error sending waitlist outreach email:", error);
      toast.error(`Failed to send outreach emails: ${error.message}`);
    } finally {
      setIsSending(false);
    }
  };

  // Fetch waitlist data from quiz_submissions collection
  useEffect(() => {
    const fetchWaitlistData = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const db = getFirestore();
        const waitlistQuery = query(
          collection(db, 'quiz_submissions'),
          orderBy('created_at', 'desc')
        );
        
        const querySnapshot = await getDocs(waitlistQuery);
        const entries = [];
        
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          entries.push({
            id: doc.id,
            ...data
          });
        });
        
        console.log('Fetched waitlist entries:', entries.length);
        setWaitlistEntries(entries);
      } catch (err) {
        console.error('Error fetching waitlist data:', err);
        setError(`Failed to load waitlist data: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    fetchWaitlistData();
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-pulse flex space-x-4">
          <div className="rounded-full bg-gray-200 dark:bg-gray-700 h-12 w-12"></div>
          <div className="flex-1 space-y-4 py-1">
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
            <div className="space-y-2">
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded"></div>
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-5/6"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <div className="rounded-md bg-red-50 dark:bg-red-900/50 p-4">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800 dark:text-red-200">
                Error Loading Waitlist Data
              </h3>
              <div className="mt-2 text-sm text-red-700 dark:text-red-300">
                <p>{error}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (waitlistEntries.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="mx-auto h-12 w-12 text-gray-400">
          <svg fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="h-12 w-12">
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3-3.75v-9A2.25 2.25 0 0014.75 4.5H6a2.25 2.25 0 00-2.25 2.25v12a2.25 2.25 0 002.25 2.25h8.25A2.25 2.25 0 0016.5 16.5v-7.5z" />
          </svg>
        </div>
        <h3 className="mt-2 text-sm font-semibold text-gray-900 dark:text-white">No waitlist submissions found</h3>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Waitlist submissions will appear here once users submit the form.
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="overflow-hidden">
        <div className="mb-4 flex justify-between items-center">
          <h2 className="text-lg font-medium text-gray-900 dark:text-white">
            Waitlist Submissions ({waitlistEntries.length})
          </h2>
          {selectedIds.length > 0 && (
            <button
              onClick={() => setShowOutreachModal(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Send Outreach Email ({selectedIds.length})
            </button>
          )}
        </div>
        
        <div className="overflow-x-auto shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-800">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  <input
                    ref={selectAllCheckboxRef}
                    type="checkbox"
                    className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    onChange={handleSelectAll}
                    checked={selectedIds.length === waitlistEntries.length && waitlistEntries.length > 0}
                  />
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Name
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Email
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  LinkedIn Profile
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Submission Date
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Resume
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
              {waitlistEntries.map((entry) => (
                <tr 
                  key={entry.id} 
                  onClick={() => handleEntryClick(entry)}
                  className="hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer transition-colors duration-150"
                >
                  <td className="px-6 py-4 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      checked={selectedIds.includes(entry.id)}
                      onChange={(e) => handleCheckboxChange(entry.id, e)}
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex flex-col">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {entry.userInfo?.firstName && entry.userInfo?.lastName 
                          ? `${entry.userInfo.firstName} ${entry.userInfo.lastName}`
                          : entry.userInfo?.firstName || entry.userInfo?.lastName || 'N/A'}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900 dark:text-white">
                      {entry.userInfo?.email || 'N/A'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm max-w-xs">
                      {entry.userInfo?.linkedinUrl ? (
                        <a
                          href={entry.userInfo.linkedinUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()} // Prevent row click when clicking link
                          className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-900 dark:hover:text-indigo-300 underline truncate block"
                          title={entry.userInfo.linkedinUrl}
                        >
                          {entry.userInfo.linkedinUrl}
                        </a>
                      ) : (
                        <span className="text-gray-500 dark:text-gray-400">N/A</span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      {formatDateOnly(entry.completedAt)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex justify-center">
                      {entry.hasResume ? (
                        <CheckIcon className="h-5 w-5 text-green-500" />
                      ) : (
                        <XMarkIcon className="h-5 w-5 text-gray-400" />
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Profile Slide Out */}
      <div className={`fixed inset-y-0 right-0 z-50 w-[600px] bg-white dark:bg-gray-900 shadow-xl transform transition-transform duration-300 ease-in-out ${
        showProfileSlideOut ? 'translate-x-0' : 'translate-x-full'
      }`}>
        {showProfileSlideOut && (
          <>
                         <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
               <div className="flex flex-col">
                 <h2 className="text-lg font-medium text-gray-900 dark:text-white">
                   {selectedEntry?.displayName || 'Unknown User'}
                 </h2>
                 <p className="text-sm text-gray-500 dark:text-gray-400">Waitlist Submission</p>
               </div>
               <button
                 onClick={handleCloseProfile}
                 className="rounded-md text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500"
               >
                 <XMarkIcon className="h-6 w-6" />
               </button>
             </div>
            <div className="flex-1 overflow-y-auto p-4">
              {selectedEntry && <CandidateProfileTab user={selectedEntry} />}
            </div>
          </>
        )}
      </div>

      {/* Backdrop */}
      {showProfileSlideOut && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-25 z-40"
          onClick={handleCloseProfile}
        />
      )}

      {/* Outreach Modal */}
      <Transition.Root show={showOutreachModal} as={Fragment}>
        <Dialog as="div" className="relative z-20" onClose={() => setShowOutreachModal(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 dark:bg-gray-900 bg-opacity-75 dark:bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-20 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                  <div>
                    <div className="absolute right-0 top-0 pr-4 pt-4">
                      <button
                        type="button"
                        className="rounded-md bg-white dark:bg-gray-800 text-gray-400 dark:text-gray-300 hover:text-gray-500 dark:hover:text-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        onClick={() => setShowOutreachModal(false)}
                      >
                        <span className="sr-only">Close</span>
                        <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                      </button>
                    </div>
                    <div className="mt-3 text-center sm:mt-5">
                      <Dialog.Title as="h3" className="text-base font-semibold leading-6 text-gray-900 dark:text-white">
                        Send Waitlist Outreach Email
                      </Dialog.Title>
                      <div className="mt-2">
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          You are about to send an outreach email to {selectedIds.length} selected user(s).
                        </p>
                      </div>
                    </div>
                    
                    <div className="mt-4">
                      <div className="rounded-md bg-gray-50 dark:bg-gray-700 p-4">
                        <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Email Preview:</div>
                        <pre className="text-xs text-gray-600 dark:text-gray-400 whitespace-pre-wrap font-mono">
                          {`Subject: Burnt 🔥 Waitlist Outreach - Let's Talk!

Hi [First Name],

Thank you for joining the waitlist for Burnt!

We're thrilled to have you as one of our earliest prospective members.

When convenient for you, I'd love to talk briefly with you and understand what would be most impactful and helpful for your career right now.

Please book a time that works for you directly on my calendar below.

Book a 15-Minute Chat (https://cal.com/grey-w/15min)

Looking forward to connecting!

Happy 4th,
Grey
Founder, Burnt`}
                        </pre>
                      </div>
                    </div>
                  </div>
                  <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
                    <button
                      type="button"
                      className="inline-flex w-full justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 sm:col-start-2"
                      onClick={handleSendOutreach}
                      disabled={isSending}
                    >
                      {isSending ? 'Sending...' : 'Send Email'}
                    </button>
                    <button
                      type="button"
                      className="mt-3 inline-flex w-full justify-center rounded-md bg-white dark:bg-gray-700 px-3 py-2 text-sm font-semibold text-gray-900 dark:text-gray-100 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 sm:col-start-1 sm:mt-0"
                      onClick={() => setShowOutreachModal(false)}
                      disabled={isSending}
                    >
                      Cancel
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </>
  );
} 